import { useState } from "react";
import { login } from "../lib/auth";
import { supabase } from "../lib/supabase";
import BrandLogo from "../components/shared/BrandLogo";
import { toast } from "@/lib/toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMounted } from "../hooks/useIsMounted";
import { BookOpen, Users, ChevronRight, Eye, EyeOff } from "lucide-react";
import FormField from "../components/forms/FormField";

const BG    = "min-h-[100dvh] pb-[env(safe-area-inset-bottom)] bg-[#f4f8fc] dark:bg-[#050d1e]";

export default function Auth() {
  const navigate     = useNavigate();
  const isMounted    = useIsMounted();
  const [searchParams] = useSearchParams();

  // If ?view=pastor is in the URL, open the login form directly
  const [view, setView] = useState(
    searchParams.get("view") === "pastor" ? "pastor" : "landing"
  );

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  /* ── Pastor login ────────────────────────────────────────────────────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);

      // Guard: if the user has no pastor/coordinator profile but has a member
      // profile, they belong to the member portal — sign them out and redirect.
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const [{ data: profile }, { data: memberProfile }] = await Promise.all([
          supabase.from("profiles").select("id, role").eq("id", user.id).maybeSingle(),
          supabase.from("member_profiles").select("id").eq("user_id", user.id).maybeSingle(),
        ]);
        if (!profile && memberProfile) {
          await supabase.auth.signOut();
          toast.error("Esta cuenta pertenece al portal de miembros. Inicia sesión en kevesapp.com/member");
          return;
        }
        if (profile && profile.role === 'member') {
          await supabase.auth.signOut();
          toast.error("Esta cuenta es de miembro de iglesia. Inicia sesión en el portal de miembros.");
          return;
        }
        if (!profile && !memberProfile) {
          await supabase.auth.signOut();
          toast.error("No se encontró un perfil para esta cuenta. Contacta al administrador.");
          return;
        }
      }
    } catch (error) {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) return;
      let message = "No se pudo iniciar sesión";
      if (error.message?.includes("Invalid login credentials"))
        message = "Correo o contraseña incorrectos";
      else if (error.message?.includes("Email not confirmed"))
        message = "Debes confirmar tu correo antes de ingresar";
      toast.error(message);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  /* ── Pastor view ─────────────────────────────────────────────────────── */
  if (view === "pastor") {
    return (
      <div className={`${BG} flex flex-col px-4`} style={{ paddingTop: "env(safe-area-inset-top)" }}>

        <div className="flex-1 flex flex-col items-center justify-center py-8">
          <div className="text-center mb-7">
            <BrandLogo variant="auto" className="w-14 h-14 mx-auto mb-2.5 object-contain" />
            <h1 className="text-[22px] font-bold text-brand dark:text-blue-400 tracking-tight">Kéves App</h1>
            <p className="text-[12px] text-slate-400 dark:text-slate-500 mt-1">Sistema de gestión pastoral adventista</p>
          </div>

          <form
            onSubmit={handleLogin}
            className="w-full max-w-md bg-white dark:bg-slate-900 rounded-[28px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] border border-slate-100 dark:border-slate-800 p-6 space-y-3"
          >
            <div className="mb-1">
              <h2 className="text-[18px] font-bold text-slate-800 dark:text-slate-100">Acceder a Kéves App</h2>
              <p className="text-[13px] text-slate-400 dark:text-slate-500 mt-0.5">Pastores y coordinadores, inicien sesión aquí</p>
            </div>

            <FormField
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={setEmail}
              required
            />
            <FormField
              type={showPass ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={setPassword}
              required
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  className="h-11 w-11 -mr-1.5 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  aria-label={showPass ? "Ocultar contraseña" : "Ver contraseña"}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-[13px] text-brand font-medium hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-brand text-white rounded-[14px] font-semibold text-[15px] shadow-sm transition-all duration-200 ease-out hover:brightness-105 active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? "Entrando..." : "Iniciar sesión"}
            </button>

            <div className="pt-1 border-t border-slate-100 dark:border-slate-800 text-center">
              <p className="text-[12px] text-slate-400 dark:text-slate-500 mb-2.5">¿Aún no tienes acceso?</p>
              <button
                type="button"
                onClick={() => navigate("/request-access")}
                className="w-full h-11 border border-brand/30 dark:border-brand/40 text-brand dark:text-blue-400 rounded-[14px] font-semibold text-[14px] hover:bg-brand/5 dark:hover:bg-brand/10 transition-all duration-200 active:scale-[0.98]"
              >
                Solicitar acceso
              </button>
            </div>
          </form>

          <p className="text-[13px] text-slate-400 dark:text-slate-500 mt-5">
            <button onClick={() => setView("landing")} className="text-brand hover:underline">
              ← Volver al inicio
            </button>
          </p>
        </div>
      </div>
    );
  }

  /* ── Landing view ────────────────────────────────────────────────────── */
  return (
    <div
      className={`${BG} flex flex-col items-center justify-center px-4`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Wordmark */}
      <div className="text-center mb-8">
        <BrandLogo variant="auto" className="w-14 h-14 mx-auto mb-2.5 object-contain" />
        <h1 className="text-[22px] font-bold text-brand dark:text-blue-400 tracking-tight">Kéves App</h1>
        <p className="text-[13px] text-slate-500 dark:text-slate-400 italic mt-1.5 leading-relaxed">
          "El Señor es mi pastor; nada me faltará."
        </p>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">— Salmos 23:1</p>
      </div>

      {/* Options */}
      <div className="w-full max-w-md space-y-3">

        <button
          onClick={() => setView("pastor")}
          className="w-full flex items-center gap-4 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700 px-5 py-4 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-200 active:scale-[0.98] active:opacity-80 text-left hover:shadow-[0_6px_20px_rgba(0,0,0,0.07)]"
        >
          <div className="h-11 w-11 rounded-[14px] bg-brand/10 dark:bg-brand/20 flex items-center justify-center shrink-0">
            <BookOpen size={20} className="text-brand dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[15px] text-slate-800 dark:text-slate-100 leading-tight">
              Soy pastor o coordinador
            </p>
            <p className="text-[12px] text-slate-400 dark:text-slate-500 mt-0.5">
              Accede con tu cuenta institucional
            </p>
          </div>
          <ChevronRight size={18} className="text-slate-300 dark:text-slate-600 shrink-0" />
        </button>

        <button
          onClick={() => navigate("/member/access")}
          className="w-full flex items-center gap-4 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700 px-5 py-4 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-200 active:scale-[0.98] active:opacity-80 text-left hover:shadow-[0_6px_20px_rgba(0,0,0,0.07)]"
        >
          <div className="h-11 w-11 rounded-[14px] bg-[#e3eefb] dark:bg-blue-900/20 flex items-center justify-center shrink-0">
            <Users size={20} className="text-brand dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[15px] text-slate-800 dark:text-slate-100 leading-tight">
              Soy miembro de iglesia
            </p>
            <p className="text-[12px] text-slate-400 dark:text-slate-500 mt-0.5">
              Accede al portal de tu iglesia
            </p>
          </div>
          <ChevronRight size={18} className="text-slate-300 dark:text-slate-600 shrink-0" />
        </button>

      </div>
    </div>
  );
}
